import { useState } from 'react';

import { getSpreadsheets } from 'services/api/google';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import Icon from '@mui/material/Icon';

// MUI Components
import { Card, TableContainer, Table, TableHead, TableRow, TableBody } from '@mui/material';

// Table Components
import DataTableHeadCell from 'examples/Tables/DataTable/DataTableHeadCell';
import DataTableBodyCell from 'examples/Tables/DataTable/DataTableBodyCell';

// Components
import FolderCell from 'layouts/projects/Pages/SingleProject/DemandPage/Sections/GooglePriceSheets/components/FolderCell';
import SheetMainCell from 'layouts/projects/Pages/SingleProject/DemandPage/Sections/GooglePriceSheets/components/SheetMainCell';
import IntegrityCell from 'layouts/projects/Pages/SingleProject/DemandPage/Sections/GooglePriceSheets/components/IntegrityCell';
import DefaultCell from 'layouts/projects/Pages/SingleProject/DemandPage/Sections/GooglePriceSheets/components/DefaultCell';
import SyncCell from 'layouts/projects/Pages/SingleProject/DemandPage/Sections/GooglePriceSheets/components/SyncCell';

import { useQuery } from '@tanstack/react-query';
import AaroLoadingScreen from 'components/Loading';

import AddSpreadsheet from 'layouts/projects/Pages/SingleProject/DemandPage/Sections/GooglePriceSheets/forms/AddSpreadsheet/index.js';
import Dialog from '@mui/material/Dialog';
import CircularProgress from '@mui/material/CircularProgress';

import { formatDateMinutes } from 'helpers/dateHelpers.js';
import { demandCalculator } from 'helpers/projectHelpers';

const fetchSpreadsheet = async (id) => {
    const response = await getSpreadsheets(`demandID=${id}`).then((rp) => (rp.data?.result ? rp.data?.result : []));
    return response;
};

export default function GooglePriceSheets(props) {
    const [openDialog, setOpenDialog] = useState(false);
    const [loading, setLoading] = useState(false);

    const { demand, project } = props;
    const { _id: demandID } = demand;
    const { data, isLoading, error } = useQuery({
        queryKey: ['fetchSpreadsheets', demandID],
        queryFn: () => fetchSpreadsheet(demandID),
        staleTime: 600000,
    });

    if (isLoading) return <AaroLoadingScreen />;

    if (error) return 'Bir Hata Oluştu: ' + error.message;

    const { numberOfItem } = demandCalculator(demand);
    const sheetImage =
        'https://upload.wikimedia.org/wikipedia/commons/thumb/3/30/Google_Sheets_logo_%282014-2020%29.svg/1498px-Google_Sheets_logo_%282014-2020%29.svg.png';
    const folderImage = 'https://icon-library.com/images/google-folder-icon/google-folder-icon-20.jpg';

    return (
        <MDBox pb={3}>
            {loading ? (
                <MDBox display="flex" justifyContent="center" alignItems="center" flexDirection="column" py={4}>
                    <MDTypography variant="text" fontWeight="medium" color="success">
                        Server entegrasyona başladı
                    </MDTypography>
                    <CircularProgress color="success" />
                </MDBox>
            ) : (
                <Card>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
                        <MDTypography variant="h6" fontWeight="medium">
                            Fiyat Çalışmaları
                        </MDTypography>
                        <MDButton
                            variant="gradient"
                            color="dark"
                            size="small"
                            iconOnly
                            onClick={() => setOpenDialog(true)}
                        >
                            <Icon>add</Icon>
                        </MDButton>
                    </MDBox>
                    <TableContainer>
                        <Table>
                            <MDBox component="thead">
                                <TableRow>
                                    <DataTableHeadCell width="auto" align="left">
                                        Fiyat Listesi
                                    </DataTableHeadCell>
                                    <DataTableHeadCell width="auto" align="left">
                                        Sheet
                                    </DataTableHeadCell>
                                    <DataTableHeadCell width="auto" align="center">
                                        Kalem
                                    </DataTableHeadCell>
                                    <DataTableHeadCell width="auto" align="center">
                                        Aktif Mi?
                                    </DataTableHeadCell>
                                    <DataTableHeadCell width="auto" align="center">
                                        Fiyatları Eşitle
                                    </DataTableHeadCell>
                                    <DataTableHeadCell width="auto" align="right">
                                        Tarih
                                    </DataTableHeadCell>
                                </TableRow>
                            </MDBox>
                            <TableBody>
                                {data?.map((item, index) => (
                                    <TableRow key={item._id || index}>
                                        <DataTableBodyCell align="left">
                                            <FolderCell
                                                image={folderImage}
                                                name={item.folderName}
                                                folderID={item.folderID}
                                            />
                                        </DataTableBodyCell>
                                        <DataTableBodyCell align="left">
                                            <SheetMainCell
                                                image={sheetImage}
                                                name={item.name}
                                                createdBy={item.createdBy?.name}
                                                spreadsheetId={item.spreadsheetId}
                                            />
                                        </DataTableBodyCell>
                                        <DataTableBodyCell align="center">
                                            <IntegrityCell
                                                currentProducts={numberOfItem}
                                                sheetProducts={item.matches?.length || 0}
                                                icon={{ color: 'error', name: 'keyboard_arrow_down' }}
                                            />
                                        </DataTableBodyCell>
                                        <DataTableBodyCell align="center">
                                            <DefaultCell>
                                                {item._id === demand?.activeSheet?._id ? 'Aktif Sheet' : 'Pasif'}
                                            </DefaultCell>
                                        </DataTableBodyCell>
                                        <DataTableBodyCell align="center">
                                            {item._id === demand?.activeSheet?._id ? (
                                                <SyncCell demandID={demandID}>Eşitle</SyncCell>
                                            ) : null}
                                        </DataTableBodyCell>
                                        <DataTableBodyCell align="right">
                                            <DefaultCell>{formatDateMinutes(item.createdAt)}</DefaultCell>
                                        </DataTableBodyCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Dialog onClose={() => setOpenDialog(false)} open={openDialog} fullWidth={true} maxWidth="sm">
                        <AddSpreadsheet
                            setOpenDialog={setOpenDialog}
                            demandID={demand._id}
                            project={project}
                            setLoading={setLoading}
                        />
                    </Dialog>
                </Card>
            )}
        </MDBox>
    );
}
